import { CBSocketClient, SocketClient } from "cbcore-ts"
import { NO, YES } from "uicore-ts"
import {
    RHOffererCompanyEligibleForPrivateMessage,
    RHProcurementPrivateMessage,
    RHProcurementPrivateMessageThreadsSnapshot
} from "../SocketClientFunctions"
import { RHCore } from "./RHCore"
import { RHSubscriptionBase } from "./RHSubscriptionBase"


// ============================================================================
// RHPrivateMessageStore
//
// Central store for procurement private messaging. Views never call
// SocketClient directly for private-message operations — they go through
// this store instead.
//
// A thread is identified by (procurementId, offererCompanyId); there is no
// separate thread record on the server. Either side may send the first message
// once the offerer company is eligible for procurement private messaging.
//
// ## Cache
// Threads are cached per procurement + active-company context. The cache is
// invalidated when the server pushes ProcurementPrivateMessagesDidChange for
// the affected procurement.
//
// ## Subscription (inherited from RHSubscriptionBase)
//   store.subscribe(this, (procurementId: string) => { ... })
//   store.unsubscribe(this)
//
// Subscribe in wasAddedToViewTree, unsubscribe in wasRemovedFromViewTree (for
// views), or viewWillAppear / viewWillDisappear (for view controllers).
// ============================================================================

export class RHPrivateMessageStore extends RHSubscriptionBase<(
    procurementId: string,
    shouldReload: boolean
) => void> {

    _threadsByProcurementAndCompanyKey = new Map<string, RHProcurementPrivateMessageThreadsSnapshot>()
    _numberOfInFlightRequestsByProcurementId = new Map<string, number>()
    _cacheGenerationByProcurementId = new Map<string, number>()

    readonly _core: RHCore

    constructor(core: RHCore) {
        super()
        this._core = core
        // Registered on the current socket client AND re-registered on every
        // socket client replacement (login/logout reconnect) — otherwise the
        // store would stop receiving pushes after an in-page login.
        this._registerSocketListeners()
        this._core._functionsToCallForEachSocketClient.push(() => this._registerSocketListeners())
    }


    // =========================================================================
    // Read API
    // =========================================================================

    /**
     * Returns the private threads visible to the caller for a procurement,
     * fetching from the server on first access and on every access after the
     * cache has been invalidated by a push message.
     */
    async retrieveThreadsForProcurement(procurementId: string): Promise<RHProcurementPrivateMessageThreadsSnapshot> {
        const actingCompanyId = await this._requiredActiveCompanyIdForPrivateMessaging()
        const cacheKey = RHPrivateMessageStore._threadCacheKey(procurementId, actingCompanyId)
        const cached = this._threadsByProcurementAndCompanyKey.get(cacheKey)
        if (cached) {
            return cached
        }

        const cacheGeneration = this._cacheGenerationForProcurement(procurementId)
        this._beginInFlightRequest(procurementId)
        try {
            const snapshot = (await SocketClient.RetrieveProcurementPrivateMessageThreads({
                procurementId,
                actingCompanyId,
                includeNotificationSnapshot: YES
            })).result

            if (cacheGeneration === this._cacheGenerationForProcurement(procurementId)) {
                this._threadsByProcurementAndCompanyKey.set(cacheKey, snapshot)
            }
            return snapshot
        }
        finally {
            this._endInFlightRequest(procurementId)
        }
    }


    /** Offerer companies the procurer may start (or continue) a private thread with. Always fresh — not cached. */
    async retrieveEligibleOffererCompanies(procurementId: string): Promise<RHOffererCompanyEligibleForPrivateMessage[]> {
        const actingCompanyId = await this._requiredActiveCompanyIdForPrivateMessaging()
        const response = await SocketClient.RetrieveOffererCompaniesEligibleForPrivateMessage({
            procurementId,
            actingCompanyId
        })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Viga pakkujate laadimisel"
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        return response.result?.companies ?? []
    }


    // =========================================================================
    // Write API
    // =========================================================================

    async sendMessage(input: {
        procurementId: string
        offererCompanyId: string
        bodyText: string
        fileIds: string[]
    }): Promise<RHProcurementPrivateMessage> {
        const actingCompanyId = await this._requiredActiveCompanyIdForPrivateMessaging()
        const response = await SocketClient.SendProcurementPrivateMessage({
            ...input,
            actingCompanyId
        })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Viga sõnumi saatmisel"
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success || !response.result.message) {
            throw new Error("Viga sõnumi saatmisel")
        }
        this.invalidate(input.procurementId)
        this._notifySubscribers(input.procurementId, YES)
        return response.result.message
    }


    /**
     * Sends the same opening message to several offerer companies at once —
     * one independent private thread per company, none of which can see the
     * others. Every company is attempted; failures are reported per company
     * instead of rejecting the whole call, since one company's rejection
     * (e.g. it no longer has a submitted offer) should not block the rest.
     */
    async startThreadsWithOffererCompanies(input: {
        procurementId: string
        offererCompanyIds: string[]
        bodyText: string
        fileIds: string[]
    }): Promise<{ companyId: string, message?: RHProcurementPrivateMessage, error?: string }[]> {
        return Promise.all(input.offererCompanyIds.map(async companyId => {
            try {
                const message = await this.sendMessage({
                    procurementId: input.procurementId,
                    offererCompanyId: companyId,
                    bodyText: input.bodyText,
                    fileIds: input.fileIds
                })
                return { companyId, message }
            }
            catch (error) {
                let errorMessage = String(error)
                if (error instanceof Error) {
                    errorMessage = error.message
                }
                return { companyId, error: errorMessage }
            }
        }))
    }


    async markThreadAsRead(procurementId: string, offererCompanyId: string): Promise<void> {
        const actingCompanyId = await this._requiredActiveCompanyIdForPrivateMessaging()
        const response = await SocketClient.MarkProcurementPrivateMessagesAsRead({
            procurementId,
            offererCompanyId,
            actingCompanyId
        })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Viga sõnumite märkimisel loetuks"
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        this.invalidate(procurementId)
        this._notifySubscribers(procurementId, YES)
    }


    // =========================================================================
    // Cache invalidation — called by socket push listener
    // =========================================================================

    /**
     * Invalidates the cached threads for the given procurement and notifies
     * all subscribers so they can reload. Called when the server pushes
     * ProcurementPrivateMessagesDidChange.
     */
    _onPrivateMessagesDidChange(procurementId: string) {
        this.invalidate(procurementId)
        this._notifySubscribers(procurementId, YES)
    }


    /**
     * Clears the cached threads for a procurement without notifying
     * subscribers. Use this before an explicit reload call so the next
     * retrieveThreadsForProcurement() goes to the server rather than
     * returning the stale cached value.
     */
    invalidate(procurementId: string) {
        this._incrementCacheGenerationForProcurement(procurementId)
        const procurementContextPrefix = `${procurementId}:`
        for (const cacheKey of this._threadsByProcurementAndCompanyKey.keys()) {
            if (cacheKey.startsWith(procurementContextPrefix)) {
                this._threadsByProcurementAndCompanyKey.delete(cacheKey)
            }
        }
    }


    clear() {
        const affectedProcurementIds = new Set<string>()
        for (const cacheKey of this._threadsByProcurementAndCompanyKey.keys()) {
            const separatorIndex = cacheKey.indexOf(":")
            if (separatorIndex > 0) {
                affectedProcurementIds.add(cacheKey.slice(0, separatorIndex))
            }
        }
        for (const procurementId of this._numberOfInFlightRequestsByProcurementId.keys()) {
            affectedProcurementIds.add(procurementId)
        }
        for (const procurementId of affectedProcurementIds) {
            this._incrementCacheGenerationForProcurement(procurementId)
        }
        this._threadsByProcurementAndCompanyKey.clear()
        for (const procurementId of affectedProcurementIds) {
            this._notifySubscribers(procurementId, NO)
        }
    }


    // =========================================================================
    // Socket push listener
    // =========================================================================

    _registerSocketListeners() {
        const socketClient = this._core.socketClient

        socketClient.addTargetForMessagesForKey(
            "ProcurementPrivateMessagesDidChange",
            (payload: { procurementId: string }) => {
                if (payload?.procurementId) {
                    this._onPrivateMessagesDidChange(payload.procurementId)
                }
            }
        )
    }


    static _threadCacheKey(procurementId: string, companyId: string): string {
        return `${procurementId}:${companyId}`
    }


    async _requiredActiveCompanyIdForPrivateMessaging(): Promise<string> {
        const companyId = await this._core.companyAffiliationStore
            .activeCompanyIdAfterLoadingAffiliations()
        if (!companyId) {
            throw new Error("Privaatse teabevahetuse jaoks tuleb valida ettevõte")
        }
        return companyId
    }


    _cacheGenerationForProcurement(procurementId: string): number {
        return this._cacheGenerationByProcurementId.get(procurementId) ?? 0
    }


    _incrementCacheGenerationForProcurement(procurementId: string) {
        this._cacheGenerationByProcurementId.set(
            procurementId,
            this._cacheGenerationForProcurement(procurementId) + 1
        )
    }


    _beginInFlightRequest(procurementId: string) {
        const numberOfInFlightRequests =
            this._numberOfInFlightRequestsByProcurementId.get(procurementId) ?? 0
        this._numberOfInFlightRequestsByProcurementId.set(
            procurementId,
            numberOfInFlightRequests + 1
        )
    }


    _endInFlightRequest(procurementId: string) {
        const numberOfInFlightRequests =
            this._numberOfInFlightRequestsByProcurementId.get(procurementId) ?? 0
        if (numberOfInFlightRequests <= 1) {
            this._numberOfInFlightRequestsByProcurementId.delete(procurementId)
            return
        }
        this._numberOfInFlightRequestsByProcurementId.set(
            procurementId,
            numberOfInFlightRequests - 1
        )
    }

}
